import { KeyboardEvent, useState } from 'react';
import { X } from 'lucide-react';

const MAX_TAGS = 20;
const MAX_TAG_LENGTH = 30;

const addTags = (tags: string[], input: string): string[] => {
  const next = [...tags];
  for (const raw of input.split(',')) {
    const tag = raw.trim().slice(0, MAX_TAG_LENGTH);
    if (tag && next.length < MAX_TAGS && !next.some((t) => t.toLowerCase() === tag.toLowerCase()))
      next.push(tag);
  }
  return next;
};

interface TagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder: string;
}

export const TagInput = ({ tags, onChange, placeholder }: TagInputProps) => {
  const [text, setText] = useState('');

  const commit = () => {
    if (!text.trim()) return;
    onChange(addTags(tags, text));
    setText('');
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && !text && tags.length) {
      onChange(tags.slice(0, -1));
    }
  };

  return (
    <div className="flex min-h-11 flex-wrap items-center gap-1.5 rounded-[0.5rem] border border-border bg-background px-2 py-1 focus-within:ring-1 focus-within:ring-ring">
      {tags.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-0.5 rounded-full border border-border bg-muted py-0.5 pl-2.5 pr-0.5 text-[12.5px] font-medium text-foreground"
        >
          {tag}
          <button
            type="button"
            className="grid h-[18px] w-[18px] place-items-center rounded-full text-muted-foreground hover:bg-background hover:text-foreground"
            aria-label={`Remove tag ${tag}`}
            onClick={() => onChange(tags.filter((t) => t !== tag))}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
        onPaste={(e) => {
          const pasted = e.clipboardData.getData('text');
          if (pasted.includes(',')) {
            e.preventDefault();
            onChange(addTags(tags, pasted));
          }
        }}
        disabled={tags.length >= MAX_TAGS}
        placeholder={tags.length ? '' : placeholder}
        className="min-w-[140px] flex-1 bg-transparent px-2 py-1.5 text-sm outline-none placeholder:text-muted-foreground"
      />
    </div>
  );
};
